import React, { useState, useEffect } from 'react';

import PropTypes from 'prop-types';
import {
  Modal,
  Form,
  Button,
  Dropdown,
  TextArea,
  Message,
  Icon,
  Divider,
  Header,
} from 'semantic-ui-react';

import _cloneDeep from 'lodash/cloneDeep';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';

import CloseableModal from 'components/common/CloseableModal';
import {
  DEFAULT_QUESTION_DATA,
  QUESTION_STATUS_OPTIONS,
  ANSWER_VALIDATION,
  QUESTION_VALIDATION,
  QUESTION_STATUS_ACTIVE,
} from 'components/modules/questions/constants';
import { QuestionGroupType, QuestionType } from 'components/modules/questions/types';
import {
  validateQuestionData,
  createNewAnswer,
  getGroupOptions,
} from 'components/modules/questions/utils';
import API from 'libs/api';

import { calculateRemainingAnswers } from './QuestionCreateEditModal.utils';
import AnswersList from './components/AnswersList';

import './QuestionCreateEditModal.scss';

const propTypes = {
  open: PropTypes.bool.isRequired,
  question: PropTypes.shape(QuestionType),
  groups: PropTypes.arrayOf(QuestionGroupType).isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
};

const defaultProps = {
  question: null,
};

export default function QuestionCreateEditModal({
  open,
  question,
  groups,
  onClose: onCloseProp,
  onSave: onSaveProp,
}) {
  const [formData, setFormData] = useState(_cloneDeep(DEFAULT_QUESTION_DATA));
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState(false);
  const [saving, setSaving] = useState(false);

  const isEditing = question !== null;
  const modalTitle = isEditing ? 'Edit Question' : 'Create Question';

  const groupOptions = getGroupOptions(groups);

  useEffect(() => {
    if (open) {
      if (isEditing) {
        setFormData({
          text: question.text || '',
          status: question.status || QUESTION_STATUS_ACTIVE,
          groups: question.groups || [],
          answers:
            question.answers && question.answers.length > 0
              ? question.answers
              : [createNewAnswer(question.id || null)],
        });
      } else {
        setFormData(_cloneDeep(DEFAULT_QUESTION_DATA));
      }
      setErrors({});
      setTouched(false);
      setSaving(false);
    }
  }, [open, question, isEditing]);

  function cleanAnswerErrors(answerIndex) {
    setErrors((prev) => ({
      ...prev,
      answerErrors: _get(prev, 'answerErrors', []).map((error, index) =>
        index === answerIndex ? null : error,
      ),
    }));
  }

  function removeAnswerError(answerIndex) {
    setErrors((prev) => ({
      ...prev,
      answerErrors: _get(prev, 'answerErrors', []).filter((_, index) => index !== answerIndex),
    }));
  }

  function onFieldChange(field, value) {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    if (touched) {
      if (errors[field]) {
        setErrors((prev) => ({
          ...prev,
          [field]: null,
        }));
      }
    }
  }

  function onAnswerChange(answerIndex, field, value) {
    setFormData((prev) => ({
      ...prev,
      answers: prev.answers.map((answer, index) =>
        index === answerIndex ? { ...answer, [field]: value } : answer,
      ),
    }));

    if (touched && _get(errors, `answerErrors[${answerIndex}]`)) {
      cleanAnswerErrors(answerIndex);
    }
  }

  function onAddAnswer() {
    const newAnswer = createNewAnswer(question?.id || null, '', formData.answers.length === 0);

    setFormData((prev) => ({
      ...prev,
      answers: [...prev.answers, newAnswer],
    }));
  }

  async function onDeleteAnswer(answerIndex) {
    if (formData.answers.length > ANSWER_VALIDATION.MIN_ANSWERS) {
      const answerToDelete = formData.answers[answerIndex];
      if (answerToDelete.id) {
        try {
          await API.delete(`/answers/${answerToDelete.id}/`);
          removeAnswerError(answerIndex);
        } catch (e) {
          setErrors((prev) => ({
            ...prev,
            answerErrors: prev.answerErrors.map((error, index) =>
              index === answerIndex ? { ...error, text: 'Failed to delete answer' } : error,
            ),
          }));
        }
      }
      const remainingAnswers = calculateRemainingAnswers(formData.answers, answerIndex);
      setFormData((prev) => ({
        ...prev,
        answers: remainingAnswers,
      }));
    }
  }

  function onPreferredAnswerChange(answerIndex) {
    setFormData((prev) => ({
      ...prev,
      answers: prev.answers.map((answer, idx) => ({
        ...answer,
        isPreferred: idx === answerIndex,
      })),
    }));
    if (errors.preferredAnswer) {
      setErrors((prev) => ({ ...prev, preferredAnswer: null }));
    }
  }

  async function onSaveClick() {
    setErrors({});
    setTouched(true);

    const validation = validateQuestionData(formData);

    if (!validation.isValid) {
      setErrors(validation.errors);
      return;
    }

    setSaving(true);

    await onSaveProp(formData);
  }

  function onCloseClick() {
    if (!saving) {
      onCloseProp();
    }
  }

  const hasErrors = !_isEmpty(errors);

  return (
    <CloseableModal
      open={open}
      onClose={onCloseClick}
      header={modalTitle}
      size="large"
      className="question-create-edit-modal"
      closeIconDisabled={saving}
    >
      <Modal.Content scrolling>
        <Form loading={saving}>
          <Form.Field error={touched && errors.text}>
            <label htmlFor="question-text">Question Text *</label>
            <TextArea
              id="question-text"
              placeholder="Enter your question..."
              value={formData.text}
              onChange={(e, { value }) => onFieldChange('text', value)}
              rows={2}
              maxLength={QUESTION_VALIDATION.MAX_LENGTH}
            />
            {touched && errors.text && <div className="field-error">{errors.text}</div>}
            <div className="character-count">
              {formData.text.length}/{QUESTION_VALIDATION.MAX_LENGTH}
            </div>
          </Form.Field>

          <Form.Field>
            <label htmlFor="question-status">Status</label>
            <Dropdown
              id="question-status"
              selection
              options={QUESTION_STATUS_OPTIONS}
              value={formData.status}
              onChange={(e, { value }) => onFieldChange('status', value)}
            />
          </Form.Field>

          <Form.Field>
            <label htmlFor="question-groups">Question Groups</label>
            <Dropdown
              id="question-groups"
              placeholder="Select groups for this question"
              fluid
              multiple
              selection
              options={groupOptions}
              value={formData.groups.map((g) => g.id)}
              onChange={(e, { value }) => {
                const selectedGroups = groups.filter((g) => value.includes(g.id));
                onFieldChange('groups', selectedGroups);
              }}
            />
          </Form.Field>

          <Divider />

          <div className="answers-section">
            <div className="answers-header">
              <Header as="h4">Answers</Header>
              <Button
                className="action-button"
                icon="plus"
                content="Add Answer"
                onClick={onAddAnswer}
              />
            </div>

            {touched && errors.answers && <Message negative content={errors.answers} />}

            {touched && errors.preferredAnswer && (
              <Message negative content={errors.preferredAnswer} />
            )}

            <AnswersList
              answers={formData.answers}
              answerErrors={errors.answerErrors}
              onAnswerChange={onAnswerChange}
              onAnswerDelete={onDeleteAnswer}
              onPreferredChange={onPreferredAnswerChange}
            />

            <div className="answers-info">
              <Icon name="info circle" />
              Only the preferred answer will be used for responding to customer questions.
            </div>
          </div>
        </Form>
      </Modal.Content>

      <Modal.Actions>
        {hasErrors && touched && (
          <Message negative content="Please fix the errors above before saving." />
        )}

        <Button
          className="action-button-gray"
          content="Cancel"
          onClick={onCloseClick}
          disabled={saving}
        />

        <Button
          className="secondary-navy"
          content={isEditing ? 'Update Question' : 'Create Question'}
          onClick={onSaveClick}
          loading={saving}
          disabled={saving}
        />
      </Modal.Actions>
    </CloseableModal>
  );
}

QuestionCreateEditModal.propTypes = propTypes;
QuestionCreateEditModal.defaultProps = defaultProps;
